import { debounce } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'antd';
import { CaretDown, Loading } from '~/ui/assets/icons';
import useOutsideClickEffect from '~/common/useOutsideClickEffect';
import { Popover, SearchBar } from '~/ui/components';
import { createPortal } from 'react-dom';
import { Input } from '../Input/Input';

import {
  ContentContainer,
  CustomComponentsAreaProps,
  MultiSelectTrigger,
  PortalSelect,
  SearchWrapper,
} from './MultiSelect.styled';

let multiSelectCounter = 0;

interface MultiSelectProps {
  options: { label: string; value: string }[];
  initialValue?: any[] | string;
  onChange?: React.Dispatch<React.SetStateAction<string[]>>;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  testId?: string;
  value?: any[] | string;
  searchable?: boolean;
}

const Portal = ({ id, ...props }: CustomComponentsAreaProps & { id: string }) =>
  createPortal(<PortalSelect id={id} {...props} />, document.getElementById('portal'));

export const MultiSelect = forwardRef(
  (
    {
      options,
      initialValue,
      label,
      onChange,
      placeholder,
      disabled,
      testId = 'cascader',
      loading,
      value,
      searchable,
    }: MultiSelectProps,
    ref,
  ) => {
    const resolveInitial = () => {
      const init = value ?? initialValue;
      if (typeof init === 'string') return [init];
      return (init as any[]) || [];
    };
    const [selectedValues, setSelectedValues] = useState<any[]>(resolveInitial);
    const isMounted = useRef(false);
    const [visible, setVisible] = useState(false);
    const [search, setSearch] = useState('');

    const [portalId] = useState(() => `mt-multiselect-portal-${++multiSelectCounter}`);

    const containerRef = useRef(null);
    const selectRef = useRef(ref);

    const searchedOptions = useMemo<any>(() => {
      if (!search?.length) {
        return options;
      }

      const filteredOptions = options?.filter((data) =>
        data?.label?.toLowerCase().includes(search.toLowerCase().trim()),
      );

      const selectedOptions = options
        .filter((option) => selectedValues.includes(option.value))
        .filter((option) => !filteredOptions.find((filteredOption) => filteredOption.value === option.value))
        .map((option) => ({
          ...option,
          disabled: !filteredOptions.find((filteredOption) => filteredOption.value === option.value),
        }));

      return [
        ...selectedOptions,
        ...filteredOptions.filter((option) => !selectedOptions.find((o) => o.value === option.value)),
      ];
    }, [options, search]);

    const hasVisibleOptions = useMemo(
      () => searchedOptions.filter((option) => !option.disabled).length > 0,
      [searchedOptions],
    );

    const handleSelectionChange = useCallback((ev) => {
      setSelectedValues(ev);
    }, []);

    const handleClear = useCallback((ev) => {
      if (!ev || ev.length === 0) {
        handleSelectionChange([]);
        setVisible(false);
      }
    }, []);

    const handleSearch = debounce((searchValue) => {
      setSearch(searchValue);
    }, 500);

    const multiSelectText = () => {
      if (loading) return '';

      const valuesToRender = selectedValues.slice(0, 3).map((el) => options.find((opt) => opt.value === el)?.label);

      if (selectedValues.length === 4) {
        return `${valuesToRender.join(', ')} (+ 1 outro)`;
      }

      if (selectedValues.length > 4) {
        return `${valuesToRender.join(', ')} (+${selectedValues.length - 3} outros)`;
      }

      if (selectedValues.length > 0) {
        return `${valuesToRender.join(', ')}`;
      }

      return '';
    };

    const getPopupContainer = () => document.getElementById(portalId);

    const getElementSize = useCallback((r) => r?.current?.clientWidth || 'auto', []);

    useEffect(() => {
      if (!isMounted.current) {
        isMounted.current = true;
        return;
      }
      onChange?.(selectedValues);
    }, [selectedValues]);

    useEffect(() => {
      const next = typeof value === 'string' ? [value] : (value as any[]) || [];
      const current = selectedValues;
      if (JSON.stringify(next) !== JSON.stringify(current)) {
        setSelectedValues(next);
      }
    }, [value]);

    useOutsideClickEffect(containerRef, () => {
      setVisible(false);
    });

    return (
      <>
        <Popover
          placement="bottomLeft"
          trigger="click"
          visible={visible}
          noHeader
          getPopupContainer={getPopupContainer}
          noArrow
          data-testid={`${testId}-multiselect`}
          content={
            <ContentContainer ref={containerRef}>
              {searchable && (
                <SearchWrapper>
                  <SearchBar onChange={handleSearch} initialValue={search} />
                </SearchWrapper>
              )}
              {!hasVisibleOptions && !loading && <p>Nenhum resultado encontrado</p>}
              {hasVisibleOptions && (
                <Checkbox.Group
                  options={searchedOptions}
                  onChange={handleSelectionChange}
                  disabled={disabled || loading}
                  value={selectedValues}
                />
              )}
            </ContentContainer>
          }
        >
          <MultiSelectTrigger
            ref={selectRef}
            disabled={disabled}
            data-active={visible ? 'true' : undefined}
            onClick={(event) => {
              const element = event.target as HTMLElement;
              if (element?.closest('.ant-input-clear-icon')) {
                return;
              }

              setVisible(true);
            }}
          >
            <Input
              ref={ref}
              placeholder={placeholder}
              allowClear
              onChange={handleClear}
              value={multiSelectText()}
              disabled={disabled || loading}
              label={label}
              testId={`${testId}-multiselect`}
              className="multiSelect-custom"
            />
            {loading ? <Loading className="icon-custom" /> : <CaretDown className="icon-custom" />}
          </MultiSelectTrigger>
        </Popover>

        <Portal id={portalId} width={getElementSize(selectRef)} />
      </>
    );
  },
);
